/* eslint-disable no-param-reassign */
import { createAppSlice } from '../../createAppSlice';
import {
  register,
  login,
  magicLogin,
  changeName,
  changeSurname,
  changeEmail,
} from '../../../services/auth.service';
import { linkUserToSession } from '../../../services/tracking.service';

const initialState = {
  isLoggedIn: false,
  name: null,
  surname: null,
  email: null,
  token: null,
  expires_in: null,
  credits: null,
  user_id: null,
  cookies: false,
  // Credit model fields
  hasPurchasedCredits: false,
  creditsExpireAt: null,
  previewCount: 0,
  isFreeUser: true,
  creditsExpired: false,
  previewRemaining: null,
  // Onboarding fields
  onboardingCompleted: false,
  onboardingIntent: null,
};

// If you are not using async thunks you can use the standalone `createSlice`.
export const authSlice = createAppSlice({
  name: 'auth',
  initialState,
  reducers: (create) => ({
    registerAction: create.asyncThunk(
      async ({
        name, surname, email, password, ip, sessionId, timezone, captchaToken,
      }) => {
        const data = await register(name, surname, email, password, ip, sessionId, timezone, captchaToken);
        return data;
      },
      {
        fulfilled: (state) => {
          state.isLoggedIn = false;
        },
        rejected: (state) => {
          state.isLoggedIn = false;
        },
      },
    ),
    loginAction: create.asyncThunk(
      async ({
        email, password, ip, sessionId, timezone,
      }) => {
        const data = await login(email, password, ip, sessionId, timezone);
        // Back-fill the anonymous page visits / events of this session with the
        // user id, so the pre-login journey ties to the account.
        if (data?.id) {
          await linkUserToSession(data.id);
        }
        return data;
      },
      {
        fulfilled: (state, action) => {
          state.isLoggedIn = true;
          state.name = action.payload.name;
          state.surname = action.payload.surname;
          state.user_id = action.payload.id;
          state.email = action.payload.user;
          state.credits = action.payload.credits;
          state.token = action.payload.access_token;
          state.expires_in = action.payload.expires_in;
          state.survey = action.payload.survey;
          // Credit model fields
          state.hasPurchasedCredits = action.payload.hasPurchasedCredits ?? false;
          state.creditsExpireAt = action.payload.credits_expire_at ?? null;
          state.previewCount = action.payload.preview_count ?? 0;
          state.isFreeUser = action.payload.is_free_user ?? true;
          state.creditsExpired = action.payload.credits_expired ?? false;
          state.previewRemaining = action.payload.preview_remaining ?? null;
        },
        rejected: (state) => {
          state.isLoggedIn = false;
          state.name = null;
          state.surname = null;
          state.user_id = null;
          state.email = null;
          state.credits = null;
          state.token = null;
          state.expires_in = null;
          state.survey = null;
          // Reset credit model fields
          state.hasPurchasedCredits = false;
          state.creditsExpireAt = null;
          state.previewCount = 0;
          state.isFreeUser = true;
          state.creditsExpired = false;
          state.previewRemaining = null;
        },
      },
    ),
    // Magic-link auto-login: same resulting session as loginAction, but the session
    // comes from exchanging a one-time token instead of email + password.
    magicLoginAction: create.asyncThunk(
      async ({ id, token }) => {
        const data = await magicLogin(id, token);
        // Same session back-fill as loginAction (magic-link path).
        if (data?.id) {
          await linkUserToSession(data.id);
        }
        return data;
      },
      {
        fulfilled: (state, action) => {
          state.isLoggedIn = true;
          state.name = action.payload.name;
          state.surname = action.payload.surname;
          state.user_id = action.payload.id;
          state.email = action.payload.user;
          state.credits = action.payload.credits;
          state.token = action.payload.access_token;
          state.expires_in = action.payload.expires_in;
          state.survey = action.payload.survey;
          // Credit model fields
          state.hasPurchasedCredits = action.payload.hasPurchasedCredits ?? false;
          state.creditsExpireAt = action.payload.credits_expire_at ?? null;
          state.previewCount = action.payload.preview_count ?? 0;
          state.isFreeUser = action.payload.is_free_user ?? true;
          state.creditsExpired = action.payload.credits_expired ?? false;
          state.previewRemaining = action.payload.preview_remaining ?? null;
        },
        rejected: (state) => {
          state.isLoggedIn = false;
          state.name = null;
          state.surname = null;
          state.user_id = null;
          state.email = null;
          state.credits = null;
          state.token = null;
          state.expires_in = null;
          state.survey = null;
          // Reset credit model fields
          state.hasPurchasedCredits = false;
          state.creditsExpireAt = null;
          state.previewCount = 0;
          state.isFreeUser = true;
          state.creditsExpired = false;
          state.previewRemaining = null;
        },
      },
    ),
    changeNameAction: create.asyncThunk(
      async ({ name, token }) => {
        await changeName(name, token);
        return { name };
      },
      {
        fulfilled: (state, action) => {
          state.name = action.payload.name;
        },
        rejected: (_state, action) => {
          throw new Error(action.error.message);
        },
      },
    ),
    changeSurnameAction: create.asyncThunk(
      async ({ surname, token }) => {
        await changeSurname(surname, token);
        return { surname };
      },
      {
        fulfilled: (state, action) => {
          state.surname = action.payload.surname;
        },
        rejected: (_state, action) => {
          throw new Error(action.error.message);
        },
      },
    ),
    changeEmailAction: create.asyncThunk(
      async ({ email, token }) => {
        await changeEmail(email, token);
        return { email };
      },
      {
        fulfilled: (state, action) => {
          state.email = action.payload.email;
        },
        rejected: (_state, action) => {
          throw new Error(action.error.message);
        },
      },
    ),
    logoutAction: create.reducer((state) => {
      state.isLoggedIn = false;
      state.name = null;
      state.surname = null;
      state.user_id = null;
      state.email = null;
      state.credits = null;
      state.token = null;
      state.expires_in = null;
      // Reset credit model fields
      state.hasPurchasedCredits = false;
      state.creditsExpireAt = null;
      state.previewCount = 0;
      state.isFreeUser = true;
      state.creditsExpired = false;
      state.previewRemaining = null;
    }),
    // A password change revokes every session and answers with a fresh token for
    // this one; a sliding-session renewal (GET /users/info `refreshedToken`,
    // components/AuthCookieSync.js) hands over a fresh one too. Either replaces
    // the stored token exactly as a login would set it.
    replaceTokenAction: create.reducer((state, action) => {
      state.token = action.payload.access_token;
      state.expires_in = action.payload.expires_in ?? state.expires_in;
    }),
    applyCookiesAction: create.reducer((state) => {
      state.cookies = true;
    }),
    setOnboardingData: create.reducer((state, action) => {
      state.onboardingCompleted = action.payload.onboardingCompleted ?? state.onboardingCompleted;
      state.onboardingIntent = action.payload.onboardingIntent ?? state.onboardingIntent;
    }),
    updateUserData: create.reducer((state, action) => {
      state.credits = action.payload.credits ?? state.credits;
      state.hasPurchasedCredits = action.payload.hasPurchasedCredits ?? state.hasPurchasedCredits;
      state.creditsExpireAt = action.payload.creditsExpireAt ?? state.creditsExpireAt;
      state.previewCount = action.payload.previewCount ?? state.previewCount;
      state.isFreeUser = action.payload.isFreeUser ?? state.isFreeUser;
      state.creditsExpired = action.payload.creditsExpired ?? state.creditsExpired;
      state.previewRemaining = action.payload.previewRemaining ?? state.previewRemaining;
    }),
  }),
  // You can define your selectors here. These selectors receive the slice
  // state as their first argument.
  selectors: {
    selectIsLoggedIn: (auth) => auth.isLoggedIn,
    selectName: (auth) => auth.name,
    selectSurname: (auth) => auth.surname,
    selectUserId: (auth) => auth.user_id,
    selectEmail: (auth) => auth.email,
    selectCredits: (auth) => auth.credits,
    selectToken: (auth) => auth.token,
    selectExpiresIn: (auth) => auth.expires_in,
    selectCookies: (auth) => auth.cookies,
    // Credit model selectors
    selectHasPurchasedCredits: (auth) => auth.hasPurchasedCredits,
    selectCreditsExpireAt: (auth) => auth.creditsExpireAt,
    selectPreviewCount: (auth) => auth.previewCount,
    selectIsFreeUser: (auth) => auth.isFreeUser,
    selectCreditsExpired: (auth) => auth.creditsExpired,
    selectPreviewRemaining: (auth) => auth.previewRemaining,
    // Onboarding selectors
    selectOnboardingCompleted: (auth) => auth.onboardingCompleted,
    selectOnboardingIntent: (auth) => auth.onboardingIntent,
  },
});

// Action creators are generated for each case reducer function.
export const {
  registerAction,
  loginAction, magicLoginAction, logoutAction, changeNameAction,
  changeSurnameAction, changeEmailAction,
  applyCookiesAction, updateUserData, setOnboardingData, replaceTokenAction,
} = authSlice.actions;

// Selectors returned by `slice.selectors` take the root state as their first argument.
export const {
  selectIsLoggedIn, selectName, selectSurname, selectCookies,
  selectUserId, selectEmail, selectCredits, selectToken, selectExpiresIn,
  selectHasPurchasedCredits, selectCreditsExpireAt, selectPreviewCount,
  selectIsFreeUser, selectCreditsExpired, selectPreviewRemaining,
  selectOnboardingCompleted, selectOnboardingIntent,
} = authSlice.selectors;

export const authReducer = authSlice.reducer;
